import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useUser } from "@/context/UserContext";
import { fetchMyFeatureFlags } from "@/services/featureFlags";

interface FeatureFlagContextType {
  loaded: boolean;
  /**
   * Resolved state of one flag. While flags haven't loaded yet, returns
   * `defaultValue` (default true) instead of false -- most flags default to
   * enabled, so failing "open" during the brief fetch avoids every user
   * seeing a nav item flash in on every page load. Server-side routes are
   * the real gate regardless of what the UI shows here.
   */
  isEnabled: (flagKey: string, defaultValue?: boolean) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({
  loaded: false,
  isEnabled: (_key: string, defaultValue = true) => defaultValue,
});

export const FeatureFlagProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: userLoading } = useUser();
  const [flags, setFlags] = useState<Record<string, boolean> | null>(null);

  useEffect(() => {
    if (userLoading) return;
    if (!user) {
      setFlags(null);
      return;
    }
    fetchMyFeatureFlags()
      .then(setFlags)
      .catch(() => setFlags(null));
  }, [user, userLoading]);

  const isEnabled = useCallback(
    (flagKey: string, defaultValue = true) => {
      if (!flags) return defaultValue;
      return flags[flagKey] ?? false;
    },
    [flags]
  );

  const value = useMemo(() => ({ loaded: flags !== null, isEnabled }), [flags, isEnabled]);

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);
